import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { StatusMessage } from 'is24-corecss'
import { animateScroll as scroll } from 'react-scroll'
import { useLocation, useNavigate } from 'react-router-dom'
import { InsertionWizardPageProps } from '../../../../@types/props-types'
import NavigationProgressBar from '../navigationProgressBar/NavigationProgressBar'
import { InitState } from '../../../../redux/reducers/root/initialState'
import { getNavigationBarScreens } from '../../../../services/screen/screenService'
import { useFormState } from './useFormState'
import { PublicationStickyFooter } from './publicationBarContent/PublicationStickyFooter'
import { useAppSelector } from '../../../../app/hooks'
import { SaveType } from '../../../../@types/types'
import { isWebView } from '../../../../utils/navigation'

const InsertionWizardPage: React.FC<InsertionWizardPageProps> = ({
  flowInformation,
  screen,
  flowScreens,
  children: content,
  onNavigate,
  onSave,
  onPreview,
  doCloseStatusMessage,
  validationMessages,
  onSaveForLater,
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { increaseSubmitCount } = useFormState()
  const messageFeature = useAppSelector((state: InitState) => state.messageFeature)
  const isSaving = useAppSelector((state: InitState) => state.insertionFeature.save === SaveType.SAVING)
  const { id, buildingId, unitId } = useAppSelector(
    (state: InitState) => state.insertionFeature.insertionIdentification
  )
  const objectChooserShown = useAppSelector(
    (state: InitState) => state.flowFeature.objectChooserInformation.objectChooserShown
  )
  const history = useNavigate()
  const hasValidGeoCoordinates = useAppSelector((state: InitState) => state.addressFeature.hasValidGeoCoordinates)
  const isNativeMobileView = isWebView()
  useEffect(() => scroll.scrollToTop(), [messageFeature.message])
  useEffect(() => {
    if (objectChooserShown) {
      const params = new URLSearchParams(window.location.search)
      buildingId && params.set('buildingId', buildingId)
      unitId && params.set('unitId', unitId)
      history(`${flowInformation.path}/${id}?${params.toString()}`)
    } else {
      history(`${flowInformation.path}/${id}${location.search}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, objectChooserShown])

  const isWaitingListFlow = flowInformation.options?.isWaitingListFlow
  const shouldShowNavigationProgressBar = !isNativeMobileView && !isWaitingListFlow
  const description = flowInformation.options?.customPageHeader?.descriptionKey
    ? t(flowInformation.options?.customPageHeader.descriptionKey)
    : undefined
  return (
    <>
      <div className={`desk-five-twelfth grid grid-flex ${isWaitingListFlow ? 'waiting-list-centered-header' : ''}`}>
        {isWaitingListFlow && description && (
          <span className='grid-item font-body-medium-regular margin-top-s'>{description}</span>
        )}
      </div>
      <div className={`grid ${isWaitingListFlow ? 'waiting-list-centered-grid' : ''}`}>
        {shouldShowNavigationProgressBar && (
          <div className={'lap-hide palm-hide'}>
            <NavigationProgressBar
              flowInformation={flowInformation}
              screens={getNavigationBarScreens(flowScreens)}
              screenNo={screen}
              validationMessages={{
                ...validationMessages,
                address: {
                  fields: {
                    ...validationMessages.address.fields!,
                    manualRegion: !hasValidGeoCoordinates ? validationMessages.address.fields?.manualRegion : [],
                    manualCity: !hasValidGeoCoordinates ? validationMessages.address.fields?.manualCity : [],
                    manualQuarter: !hasValidGeoCoordinates ? validationMessages.address.fields?.manualQuarter : [],
                  },
                  validated: validationMessages.address.validated,
                },
              }}
              onNavigate={onNavigate}
            />
          </div>
        )}
        <div
          className={`grid-item palm-one-whole lap-one-whole ${isWaitingListFlow ? '' : 'desk-padding-left-xxl lap-padding-left-l lap-padding-right-l'} mf-form ${isNativeMobileView ? 'desk-one-whole desk-padding-right-xxl palm-padding-bottom-xxxl' : 'desk-three-fourth'}`}
        >
          <div className='lap-margin-left-m lap-margin-right-m'>
            {messageFeature.message && (
              <div className='desk-one-half'>
                <StatusMessage
                  data-testid='status-message'
                  className='margin-bottom-xxl'
                  statusType='info'
                  onClose={doCloseStatusMessage}
                  headline={null}
                  message={t(`statusMessage.messages.${messageFeature.message}`, 'Message', {
                    ...messageFeature.values,
                  })}
                />
              </div>
            )}
            <div
              className={`font-gray font-body-small-regular padding-bottom-spacing-600${isNativeMobileView || isWaitingListFlow ? ' padding-top-spacing-600' : ''}`}
            >
              {t('message.mandatory')}
            </div>
            {content}
          </div>
        </div>
        <div className='grid-item sticky-publication-bar'>
          <PublicationStickyFooter
            isSaving={isSaving}
            isNativeMobileView={isNativeMobileView}
            isWaitingListFlow={isWaitingListFlow}
            onPreview={onPreview}
            onSave={onSave}
            onSaveForLater={onSaveForLater}
            increaseSubmitCount={increaseSubmitCount}
          />
        </div>
      </div>
    </>
  )
}

export default InsertionWizardPage
